// ACTA_AGENT_DAILY_PANEL_V1
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowClockwise, CalendarBlank, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { gmao as gmaoApi, prospects as prospectsApi } from "@/api";
import { formatApiError } from "@/lib/api";
import { availableAgentActions } from "@/agent/actionRegistry";
import { hasPageAccess } from "@/lib/accessPermissions";
import { actionTimeLabel, buildDailyActionItems } from "@/agent/dailyActions";

export default function AgentDailyPanel({ active, user, hasFeature, onOpenItem }) {
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [completionError, setCompletionError] = useState("");

  const available = useMemo(
    () => availableAgentActions({ user, hasFeature }),
    [user, hasFeature]
  );
  const commerce = available.some((item) => item.id === "navigation.agenda");
  const gmao = available.some((item) => item.id === "navigation.gmao.planning");

  const refresh = useCallback(async () => {
    if (!commerce && !gmao) {
      setItems([]);
      setErrors([]);
      return;
    }
    setLoading(true);
    const sources = [
      ...(commerce ? [{ name: "Agenda commercial", load: () => prospectsApi.agenda() }] : []),
      ...(gmao ? [{ name: "Planning GMAO", load: () => gmaoApi.listInterventions({ limit: 500 }) }] : []),
    ];
    const results = await Promise.allSettled(sources.map((source) => source.load()));
    const loaded = { agenda: [], interventions: [] };
    const failures = [];
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        failures.push(sources[index].name + " : " + (
          formatApiError(result.reason?.response?.data?.detail) || "chargement indisponible"
        ));
      } else if (sources[index].name === "Agenda commercial") {
        loaded.agenda = Array.isArray(result.value) ? result.value : [];
      } else {
        loaded.interventions = Array.isArray(result.value) ? result.value : [];
      }
    });
    setItems(buildDailyActionItems({ ...loaded, canOpenInstallations: hasPageAccess(user, "gmao", "installations"), now: new Date() }));
    setErrors(failures);
    setLoading(false);
  }, [commerce, gmao, user]);

  useEffect(() => {
    if (active) {
      setPending(null);
      setCompletionError("");
      refresh();
    }
  }, [active, refresh, user?.id]);

  const complete = async () => {
    if (!pending?.noteId || completing) return;
    setCompleting(true);
    setCompletionError("");
    try {
      if (pending.actionType === "followup") {
        await prospectsApi.completeFollowup(pending.noteId);
      } else {
        await prospectsApi.completeAction(pending.noteId);
      }
      setPending(null);
      await refresh();
    } catch (error) {
      setCompletionError(formatApiError(error?.response?.data?.detail) || "Impossible de terminer cette action.");
    } finally {
      setCompleting(false);
    }
  };

  const overdue = items.filter((item) => item.overdue);
  const today = items.filter((item) => !item.overdue);

  const renderItems = (rows) => rows.map((item) => (
    <article key={item.key} className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3" data-testid="acta-agent-daily-item">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="text-[11px] font-semibold text-[var(--do-muted)]">{item.origin} · {actionTimeLabel(item)}</span>
          <div className="mt-1 text-sm font-bold text-[var(--do-ink)]">{item.label}</div>
          <div className="mt-0.5 text-xs text-[var(--do-ink-soft)]">{item.subject}</div>
        </div>
        {item.overdue && <span className="shrink-0 text-xs font-bold text-[var(--do-warning)]">En retard</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => onOpenItem(item)} className="min-h-9 rounded-lg border border-[var(--do-border-strong)] px-3 text-xs font-bold text-[var(--do-ink)]" data-testid="acta-agent-daily-open">
          Ouvrir et agir
        </button>
        {item.noteId && !pending && (
          <button type="button" onClick={() => setPending(item)} className="min-h-9 rounded-lg bg-[var(--do-accent-soft)] px-3 text-xs font-bold text-[var(--do-ink)]" data-testid="acta-agent-daily-complete-request">
            Marquer comme réalisée
          </button>
        )}
      </div>
      {pending?.key === item.key && (
        <div className="mt-3 rounded-lg border border-[var(--do-border-strong)] bg-[var(--do-bg-2)] p-3" data-testid="acta-agent-daily-confirmation">
          <p className="text-xs font-medium text-[var(--do-ink)]">Confirmer la clôture de cette action commerciale ?</p>
          <div className="mt-2 flex gap-2">
            <button type="button" disabled={completing} onClick={() => setPending(null)} className="min-h-9 rounded-lg border border-[var(--do-border)] px-3 text-xs">Annuler</button>
            <button type="button" disabled={completing} onClick={complete} className="min-h-9 rounded-lg bg-[var(--do-accent-strong)] px-3 text-xs font-bold text-[var(--do-bg-solid)]" data-testid="acta-agent-daily-complete-confirm">
              {completing ? "Enregistrement…" : "Confirmer"}
            </button>
          </div>
          {completionError && <p role="alert" className="mt-2 text-xs text-[var(--do-danger)]">{completionError}</p>}
        </div>
      )}
    </article>
  ));

  return (
    <div className="space-y-4" data-testid="acta-agent-daily-panel">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-base font-bold text-[var(--do-ink)]">Votre journée</h3>
          <p className="text-xs text-[var(--do-muted)]">Actions datées de votre périmètre, depuis les modules ACTA.</p>
        </div>
        <button type="button" disabled={loading} onClick={refresh} aria-label="Actualiser les actions" className="flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--do-border)] text-[var(--do-ink)]" data-testid="acta-agent-daily-refresh">
          <ArrowClockwise size={18} />
        </button>
      </div>
      {errors.map((error) => <p key={error} role="alert" className="rounded-lg bg-[var(--do-warning-soft)] p-2 text-xs text-[var(--do-ink)]"><WarningCircle className="mr-1 inline" size={16} />{error}</p>)}
      {loading && <p className="text-sm text-[var(--do-muted)]">Chargement des actions…</p>}
      {!loading && !items.length && !errors.length && <div className="rounded-xl border border-[var(--do-border)] p-6 text-center text-sm text-[var(--do-muted)]">Aucune action datée aujourd’hui ou en retard dans vos modules accessibles.</div>}
      {!loading && !items.length && !!errors.length && <div className="text-xs text-[var(--do-muted)]">Les sources disponibles n’ont renvoyé aucune action.</div>}
      {!loading && overdue.length > 0 && <section className="space-y-2"><h4 className="flex items-center gap-2 text-sm font-bold text-[var(--do-ink)]"><WarningCircle size={17} />En retard ({overdue.length})</h4>{renderItems(overdue)}</section>}
      {!loading && today.length > 0 && <section className="space-y-2"><h4 className="flex items-center gap-2 text-sm font-bold text-[var(--do-ink)]"><CalendarBlank size={17} />Aujourd’hui ({today.length})</h4>{renderItems(today)}</section>}
      {!commerce && !gmao && <div className="flex items-start gap-2 text-xs text-[var(--do-muted)]"><CheckCircle size={16} />Aucun agenda accessible pour ce rôle.</div>}
    </div>
  );
}
